import { hash, verify } from '@node-rs/argon2';
import { HttpError } from '../../core/errors.js';

// argon2id (the library default is Argon2id). Parameters follow the OWASP minimums.
const OPTIONS = { memoryCost: 19456, timeCost: 2, parallelism: 1 };

export const hashPassword = (plain: string) => hash(plain, OPTIONS);

export async function verifyPassword(hashed: string, plain: string): Promise<boolean> {
  try {
    return await verify(hashed, plain);
  } catch {
    return false;
  }
}

/** Hash of a throwaway password, used so unknown-email logins cost the same time as real ones. */
let dummy: Promise<string> | undefined;
export const dummyHash = () => (dummy ??= hashPassword('not-a-real-password-timing-only'));

const COMMON = new Set([
  'password1234',
  'password12345',
  '123456789012',
  'qwertyuiop12',
  'letmein12345',
  'welcome12345',
  'administrator',
  'iloveyou1234',
]);

/** Length >= 12 (PLAN.md §5), plus a small block-list and a variety check. */
export function assertStrongPassword(
  password: string,
  context: { email?: string; name?: string } = {},
) {
  const problems: string[] = [];
  if (password.length < 12) problems.push('must be at least 12 characters');
  if (password.length > 128) problems.push('must be at most 128 characters');
  if (COMMON.has(password.toLowerCase())) problems.push('is too common');
  if (new Set(password).size < 5) problems.push('is too repetitive');
  const local = context.email?.split('@')[0]?.toLowerCase();
  if (local && local.length >= 4 && password.toLowerCase().includes(local)) {
    problems.push('must not contain your email name');
  }
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length;
  if (classes < 3)
    problems.push('must mix at least three of: lowercase, uppercase, digits, symbols');
  if (problems.length) {
    throw new HttpError(400, 'WEAK_PASSWORD', `Password ${problems.join(', ')}`);
  }
}
